import { useNavigate } from 'react-router';

import { IncoTerms } from '@invenco/sales-interface/interfaces';
import type { WarehouseCarrierServiceDTO } from '@invenco/warehousing-interface';

import { DialogBody, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { SelectItem } from '@/components/core/select';
import { useDialogContext } from '@/components/dialog-with-context';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { SelectFormField } from '@/components/forms/select-form-field';
import { TagsFormField } from '@/components/forms/tags-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import { useGatewayMutation } from '@/shared/hooks/queries';

import {
  DEFAULT_CARRIER_ACCOUNT_ID,
  DEFAULT_SHIPPING_METHOD_ID,
  getFormValues,
  renderShippingMethodName,
  useWarehouseCarrierServiceForm,
} from '../warehouse-carrier-service-form';

export function CreateWarehouseCarrierServiceDialogContent() {
  const { warehousingGateway } = useGateways();
  const { isOpen } = useDialogContext();
  const navigate = useNavigate();

  const { mutate: createWarehouseCarrierService } = useGatewayMutation({
    mutationFn: (warehouseCarrierService: Partial<WarehouseCarrierServiceDTO>) =>
      warehousingGateway.createWarehouseCarrierService(warehouseCarrierService),
    linkedQuery: ['warehouseCarrierServices'],
    linkResponseToQuery: false,
    successMessage: 'Warehouse carrier service created',
    onSuccess: (warehouseCarrierService) => {
      void navigate(`/warehouse-carrier-services/${warehouseCarrierService.id}`);
    },
  });

  const {
    form,
    carriers,
    isLoadingCarriers,
    wmsInstances,
    isLoadingWmsInstances,
    carrierAccounts,
    isLoadingCarrierAccounts,
    filteredShippingMethods,
    isLoadingShippingMethods,
    selectedCarrier,
    selectedCarrierService,
    selectedCarrierId,
    selectedCarrierServiceId,
    selectedCarrierAccountId,
  } = useWarehouseCarrierServiceForm({ defaultValues: getFormValues(), enabled: isOpen });

  return (
    <FormDialogContent
      form={form}
      onSubmit={(data) =>
        createWarehouseCarrierService({
          wmsInstanceId: data.wmsInstanceId,
          codes: [
            { code: data.primaryCode, isPrimary: true },
            ...data.secondaryCodes.map((code) => ({ code, isPrimary: false })),
          ],
          carrierCode: selectedCarrier?.code,
          carrierServiceCode: selectedCarrierService?.code,
          incoTerms: data.incoTerms,
          signatureRequired: data.signatureRequired === 'true',
          shippingMethodId:
            !data.shippingMethodId || data.shippingMethodId === DEFAULT_SHIPPING_METHOD_ID
              ? ''
              : data.shippingMethodId,
        })
      }
    >
      <DialogHeader>
        <DialogTitle>Create warehouse carrier service</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <div className="grid gap-4">
          <SelectFormField
            control={form.control}
            name="wmsInstanceId"
            label="WMS instance"
            className="w-full min-w-0"
            placeholder={isLoadingWmsInstances ? 'Loading WMS instances' : 'Select WMS instance'}
          >
            {wmsInstances?.map((wmsInstance) => (
              <SelectItem key={wmsInstance.id} value={wmsInstance.id}>
                {wmsInstance.code}
              </SelectItem>
            ))}
          </SelectFormField>
          <SelectFormField
            control={form.control}
            name="carrierId"
            label="Carrier"
            className="w-full min-w-0"
            placeholder={isLoadingCarriers ? 'Loading carriers' : 'Select carrier'}
          >
            {carriers.map((carrier) => (
              <SelectItem key={carrier.id} value={carrier.id}>
                {carrier.name}
              </SelectItem>
            ))}
          </SelectFormField>
          <SelectFormField
            control={form.control}
            name="carrierServiceId"
            label="Carrier service"
            className="w-full min-w-0"
            disabled={!selectedCarrierId}
          >
            {selectedCarrier?.carrierServices?.map((carrierService) => (
              <SelectItem key={carrierService.id} value={carrierService.id}>
                {carrierService.name}
              </SelectItem>
            ))}
          </SelectFormField>
          <SelectFormField
            control={form.control}
            name="carrierAccountId"
            label="Carrier account"
            className="w-full min-w-0"
            disabled={!selectedCarrierId || isLoadingCarrierAccounts}
          >
            <SelectItem value={DEFAULT_CARRIER_ACCOUNT_ID}>Default</SelectItem>
            {carrierAccounts?.map((carrierAccount) => (
              <SelectItem key={carrierAccount.id} value={carrierAccount.id}>
                {carrierAccount.name}
              </SelectItem>
            ))}
          </SelectFormField>
          <SelectFormField
            control={form.control}
            name="shippingMethodId"
            label="Shipping method"
            className="w-full min-w-0"
            disabled={
              !selectedCarrierServiceId ||
              selectedCarrierAccountId === DEFAULT_CARRIER_ACCOUNT_ID ||
              isLoadingShippingMethods
            }
          >
            <SelectItem value={DEFAULT_SHIPPING_METHOD_ID}>Default</SelectItem>
            {filteredShippingMethods?.map((shippingMethod) => (
              <SelectItem key={shippingMethod.id} value={shippingMethod.id}>
                {renderShippingMethodName(shippingMethod)}
              </SelectItem>
            ))}
          </SelectFormField>
          <SelectFormField
            control={form.control}
            name="signatureRequired"
            label="Signature required"
            className="w-full min-w-0"
          >
            <SelectItem value="true">Yes</SelectItem>
            <SelectItem value="false">No</SelectItem>
          </SelectFormField>
          <SelectFormField
            control={form.control}
            name="incoTerms"
            label="Inco terms"
            className="w-full min-w-0"
          >
            <SelectItem value={IncoTerms.DDU}>DDU</SelectItem>
            <SelectItem value={IncoTerms.DDP}>DDP</SelectItem>
          </SelectFormField>
          <TextFormField control={form.control} name="primaryCode" label="Primary export code" />
          <TagsFormField
            control={form.control}
            name="secondaryCodes"
            label="Import codes"
            caseSensitive
          />
        </div>
      </DialogBody>
    </FormDialogContent>
  );
}
